/**
 * Floating Language Switcher Component
 * ZO Bull Algo Trading Platform
 * 
 * A floating button that users can click to change language anytime
 * Version: 6.2.0
 */
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AVAILABLE_LANGUAGES, RTL_LANGUAGES } from '../i18n';
import './FloatingLanguageSwitcher.css';

const FloatingLanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentLang = AVAILABLE_LANGUAGES.find(l => l.code === i18n.language) || AVAILABLE_LANGUAGES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Apply RTL when language changes
  useEffect(() => {
    const lang = i18n.language;
    document.documentElement.dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [i18n.language]);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setIsOpen(false);
    setSearchQuery('');
  };

  const filteredLanguages = AVAILABLE_LANGUAGES.filter(lang =>
    lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.nativeName.includes(searchQuery) ||
    lang.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group languages by category
  const indianLanguages = filteredLanguages.filter(l => 
    ['hi', 'hi-en', 'mr', 'ta', 'te', 'kn', 'ml', 'bn', 'gu', 'pa', 'or', 'as', 'bho', 'mai', 'ne', 'sa', 'ur', 'ks', 'kok', 'mni', 'brx', 'sat', 'doi'].includes(l.code)
  );
  
  const internationalLanguages = filteredLanguages.filter(l => 
    !['hi', 'hi-en', 'mr', 'ta', 'te', 'kn', 'ml', 'bn', 'gu', 'pa', 'or', 'as', 'bho', 'mai', 'ne', 'sa', 'ur', 'ks', 'kok', 'mni', 'brx', 'sat', 'doi'].includes(l.code)
  );

  return (
    <div className="floating-lang-switcher" ref={dropdownRef}>
      {/* Floating Button */}
      <button 
        className="floating-lang-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
        title={t('Change language')}
      >
        <span className="lang-btn-icon">🌐</span>
        <span className="lang-btn-code">{currentLang.code.toUpperCase()}</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="floating-lang-dropdown">
          <div className="floating-lang-header">
            <h4>🌍 {t('Select Language')}</h4>
            <button 
              className="lang-close-btn"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>

          <div className="floating-lang-search">
            <input
              type="text"
              placeholder="Search language..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          <div className="floating-lang-list">
            {/* Indian Languages */}
            {indianLanguages.length > 0 && (
              <div className="lang-section">
                <h5>🇮🇳 {t('Indian Languages')}</h5>
                <div className="lang-options">
                  {indianLanguages.map(lang => (
                    <button
                      key={lang.code}
                      className={`lang-option ${i18n.language === lang.code ? 'active' : ''}`}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      <span className="lang-flag">{lang.flag}</span>
                      <div className="lang-details">
                        <span className="lang-native">{lang.nativeName}</span>
                        <span className="lang-name">{lang.name}</span>
                      </div>
                      {i18n.language === lang.code && (
                        <span className="lang-check">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* International Languages */}
            {internationalLanguages.length > 0 && (
              <div className="lang-section">
                <h5>🌍 {t('International Languages')}</h5>
                <div className="lang-options">
                  {internationalLanguages.map(lang => (
                    <button
                      key={lang.code}
                      className={`lang-option ${i18n.language === lang.code ? 'active' : ''}`}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      <span className="lang-flag">{lang.flag}</span>
                      <div className="lang-details">
                        <span className="lang-native">{lang.nativeName}</span>
                        <span className="lang-name">{lang.name}</span>
                      </div>
                      {i18n.language === lang.code && (
                        <span className="lang-check">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="floating-lang-footer">
            <span className="lang-count">
              {AVAILABLE_LANGUAGES.length} {t('languages available')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingLanguageSwitcher;
